/**
 * Form for generating a dance using AI
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGenerateDance, useDances } from '../hooks/useDances';
import type { GenerateDanceRequest } from '../api/danceApi';

export function AIPromptForm() {
  const navigate = useNavigate();
  const generateDance = useGenerateDance();
  const { data: allDances, isLoading: isDancesLoading } = useDances('ALL');

  const [formData, setFormData] = useState<GenerateDanceRequest>({
    prompt: '',
    difficulty: undefined,
    style: undefined,
    seedDanceIds: [],
    energyLevel: undefined,
    programRole: undefined,
  });

  const [searchParams] = useSearchParams();
  const [selectedSeeds, setSelectedSeeds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const prompt = searchParams.get('prompt');
    const difficulty = searchParams.get('difficulty');
    const style = searchParams.get('style');
    const energyLevel = searchParams.get('energyLevel');
    const programRole = searchParams.get('programRole');
    if (prompt || difficulty || style || energyLevel || programRole) {
      setFormData(prev => ({
        ...prev,
        ...(prompt && { prompt }),
        ...(difficulty && { difficulty: difficulty as any }),
        ...(style && { style: style as any }),
        ...(energyLevel && { energyLevel: energyLevel as any }),
        ...(programRole && { programRole: programRole as any }),
      }));
    }
  }, [searchParams]);

  const handleSeedToggle = (danceId: string) => {
    const newSelected = new Set(selectedSeeds);
    if (newSelected.has(danceId)) {
      newSelected.delete(danceId);
    } else {
      if (newSelected.size >= 3) {
        return; // Limit to 3 selections
      }
      newSelected.add(danceId);
    }
    setSelectedSeeds(newSelected);
    setFormData({ ...formData, seedDanceIds: Array.from(newSelected) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await generateDance.mutateAsync(formData);
      navigate(`/dances/${response.dance.danceId}`, {
        state: { validationScore: response.validationScore, attempt: response.attempt }
      });
    } catch (error) {
      console.error('Failed to generate dance:', error);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    fontSize: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: 'var(--radius-sm)',
    transition: 'border-color var(--transition-base)',
    fontFamily: 'inherit'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 'var(--spacing-sm)',
    fontWeight: 'bold',
    color: 'var(--color-text)'
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <form onSubmit={handleSubmit} className="card" style={{ backgroundColor: 'white', padding: 'var(--spacing-xl)' }}>
        <h2 style={{ marginTop: 0, marginBottom: 'var(--spacing-sm)' }}>Generate Dance with AI</h2>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-lg)' }}>
          Describe the kind of dance you'd like to create, and AI will generate a contra dance in standard A1/A2/B1/B2 notation.
        </p>

        <div style={{
          padding: 'var(--spacing-md)',
          marginBottom: 'var(--spacing-xl)',
          backgroundColor: 'var(--color-ai-bg)',
          borderLeft: '4px solid var(--color-ai-text)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.875rem'
        }}>
          <strong>Note:</strong> All generated dances use gender-neutral terminology (Larks and Robins) instead of gendered terms.
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="prompt" style={labelStyle}>
            Prompt *
          </label>
          <textarea
            id="prompt"
            required
            value={formData.prompt}
            onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
            rows={5}
            placeholder="e.g., Create a flowing contra dance for beginners with a neighbor swing to start, featuring long lines and a partner swing in B1. Use gender-neutral terminology (Larks/Robins)."
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>
          <div>
            <label htmlFor="difficulty" style={labelStyle}>Difficulty</label>
            <select
              id="difficulty"
              value={formData.difficulty || ''}
              onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as any || undefined })}
              style={inputStyle}
            >
              <option value="">Any</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div>
            <label htmlFor="style" style={labelStyle}>Style</label>
            <select
              id="style"
              value={formData.style || ''}
              onChange={(e) => setFormData({ ...formData, style: e.target.value as any || undefined })}
              style={inputStyle}
            >
              <option value="">Any</option>
              <option value="contra">Contra</option>
              <option value="square">Square</option>
              <option value="english-country">English Country</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="energyLevel" style={labelStyle}>Energy Level</label>
            <select
              id="energyLevel"
              value={formData.energyLevel || ''}
              onChange={(e) => setFormData({ ...formData, energyLevel: e.target.value as any || undefined })}
              style={inputStyle}
            >
              <option value="">Any</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label htmlFor="programRole" style={labelStyle}>Program Role</label>
            <select
              id="programRole"
              value={formData.programRole || ''}
              onChange={(e) => setFormData({ ...formData, programRole: e.target.value as any || undefined })}
              style={inputStyle}
            >
              <option value="">Any</option>
              <option value="opener">Opener</option>
              <option value="filler">Filler</option>
              <option value="closer">Closer</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
          <label style={labelStyle}>
            Seed Dances (optional, max 3)
          </label>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-md)' }}>
            Select existing dances to inspire the AI generation
          </p>

          {isDancesLoading ? (
            <div style={{ padding: 'var(--spacing-md)', color: 'var(--color-text-secondary)' }}>Loading dances...</div>
          ) : allDances && allDances.length > 0 ? (
            <div style={{
              maxHeight: '250px',
              overflowY: 'auto',
              border: '2px solid #e0e0e0',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--spacing-xs)'
            }}>
              {allDances.map((dance) => (
                <label
                  key={dance.danceId}
                  style={{
                    display: 'block',
                    padding: 'var(--spacing-sm)',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: selectedSeeds.has(dance.danceId) ? 'var(--color-ai-bg)' : 'transparent',
                    transition: 'background-color var(--transition-base)',
                    borderRadius: 'var(--radius-sm)'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedSeeds.has(dance.danceId)}
                    onChange={() => handleSeedToggle(dance.danceId)}
                    disabled={!selectedSeeds.has(dance.danceId) && selectedSeeds.size >= 3}
                    style={{ marginRight: 'var(--spacing-sm)' }}
                  />
                  <strong>{dance.title}</strong>
                  {dance.choreographer && ` by ${dance.choreographer}`}
                  {dance.difficulty && <span className="badge badge-difficulty" style={{ marginLeft: 'var(--spacing-sm)' }}>{dance.difficulty}</span>}
                </label>
              ))}
            </div>
          ) : (
            <div style={{ padding: 'var(--spacing-md)', color: 'var(--color-text-secondary)', fontStyle: 'italic', border: '2px solid #e0e0e0', borderRadius: 'var(--radius-sm)' }}>
              No dances available yet. Create some dances first to use as seed inspiration.
            </div>
          )}
          {selectedSeeds.size > 0 && (
            <div style={{ marginTop: 'var(--spacing-sm)', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Selected: {selectedSeeds.size} of 3
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={generateDance.isPending}
            className="btn"
            style={{ backgroundColor: 'var(--color-success)', color: 'white' }}
          >
            {generateDance.isPending ? 'Generating...' : 'Generate Dance'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>

        {generateDance.isPending && (
          <div style={{
            marginTop: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: '#d1ecf1',
            color: '#0c5460',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid #0c5460'
          }}>
            Generating dance... This may take 5-10 seconds.
          </div>
        )}

        {generateDance.isError && (
          <div style={{
            marginTop: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid var(--color-danger)'
          }}>
            <strong>Error:</strong> {generateDance.error.message}
          </div>
        )}
      </form>
    </div>
  );
}
